// PR-31: action breadcrumbs ring buffer.
//
// Errors are easier to triage when you can see what the user did in
// the seconds before the failure. We keep a small ring of recent
// "things that happened" and dump it into every error report:
//
//   click   — user clicked something (capture-phase, before any
//             handler can stop propagation). Records the element
//             role / text / id so the triager can spot which button.
//   fetch   — outgoing API call started; the matching done/failed
//             entry shows status + duration. Lets you see "the user
//             clicked Send → POST /messages started → 0ms later
//             unhandledrejection fired" — the chain that points at
//             a fetch interceptor.
//   log     — explicit programmatic crumb (for narrating an
//             internal multi-step action that doesn't naturally map
//             to click+fetch, e.g. "drained 5 queued captures").
//
// Why a ring buffer (vs. growing array): a long-running tab can
// accumulate thousands of clicks; we only ever care about the last
// dozen-or-so before an error fires. Cap at 30 so the dump is small
// (each crumb is <200 bytes; 30 × 200 = ~6KB per error row).
//
// Capture phase on `click` (`useCapture: true`) so we run BEFORE any
// app handler — even if a handler stops propagation we still get the
// crumb. Same reasoning as Sentry's auto-instrumentation.

export type CrumbKind = 'click' | 'fetch' | 'route' | 'log'

export interface Crumb {
  /** ms since epoch — lets the triager order against the error timestamp. */
  t: number
  kind: CrumbKind
  data: Record<string, unknown>
}

const RING: Crumb[] = []
const MAX = 30

export function crumb(kind: CrumbKind, data: Record<string, unknown>): void {
  RING.push({ t: Date.now(), kind, data })
  if (RING.length > MAX) RING.shift()
}

export function recentCrumbs(): Crumb[] {
  return [...RING]
}

/**
 * Best-effort element label for a click crumb. Order: aria-label →
 * id → text content (truncated) → tag. Mirrors what a human would
 * say if asked "what did you click?".
 */
function describeElement(el: Element): Record<string, unknown> {
  const tag = el.tagName.toLowerCase()
  const role = el.getAttribute('role') ?? null
  const ariaLabel = el.getAttribute('aria-label') ?? null
  const id = el.id || null
  // Cap text — buttons with long labels (e.g. a card title) blow up
  // the crumb payload.
  const text = (el.textContent ?? '').trim().slice(0, 60) || null
  return { tag, role, aria_label: ariaLabel, id, text }
}

/**
 * Wire up auto-instrumented breadcrumbs. Idempotent — call once from
 * main.tsx before render. Click capture runs at the document root so
 * it sees events that target any descendant.
 */
export function installBreadcrumbs(): void {
  if (typeof window === 'undefined') return
  document.addEventListener(
    'click',
    (e) => {
      const target = e.target
      if (target instanceof Element) {
        // Walk up to the nearest button/link/role-bearing element so
        // a click on a child <span> resolves to its <button> wrapper.
        const labelable = target.closest(
          'button, a, [role], [onclick], input, select, textarea',
        )
        crumb('click', describeElement(labelable ?? target))
      }
    },
    { capture: true, passive: true },
  )
}
